import { Box, Button, Container, Icon, IconButton, Link, Text } from '@chakra-ui/react';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaEyeSlash } from '@react-icons/all-files/fa/FaEyeSlash';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { Link as RRLink } from 'react-router-dom';
import * as Yup from 'yup';

import DividerWithText from '../../components/DividerWithText';
import SimpleFooter from '../../components/SimpleFooter';
import Input from '../../components/formComponents/Input';
import { LDFlags } from '../../constants/flags';
import { CREATE_ACCOUNT, FORGOT_PASSWORD } from '../../constants/urls';
import { useFlag } from '../../hooks';
import { RudderEvent, rudderanalytics } from '../../services/analytics/rudderstack';
import { login } from '../../services/auth0';
import { emailValidationRequired } from '../../utils/validation/email';
import Title from './components/Title';

interface Values {
  email: string;
  password: string;
}

const validationSchema = Yup.object({
  email: emailValidationRequired,
  password: Yup.string().required('Please enter a value'),
});

type Props = {
  setShowOneClickLogin: (a: boolean) => void;
  setPassedEmail: (email: string) => void;
};

const PasswordLogin = ({ setShowOneClickLogin, setPassedEmail }: Props) => {
  const passwordlessDashboardAccessEnabled = useFlag(LDFlags.PASSWORDLESS_DASHBOARD_ACCESS);

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (values: Values, helpers: FormikHelpers<Values>) => {
    setError('');

    login({
      email: values.email,
      password: values.password,
      callback: (err) => {
        helpers.setSubmitting(false);
        const errorMessage = err?.description ?? err?.toString() ?? 'Invalid credentials';

        rudderanalytics.track(RudderEvent.Error, {
          error_message: errorMessage,
          email: values.email,
        });

        if (err) {
          setError(errorMessage);
        }
      },
    });
  };

  return (
    <>
      <Container pt={10}>
        <Title>Welcome Back to Lease End</Title>
        <Formik
          enableReinitialize
          initialValues={{
            email: '',
            password: '',
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting }) => {
            return (
              <Form>
                <Box bg="white" p={8} rounded="lg" maxW="400px" mx="auto">
                  <Input
                    label="Email Address"
                    name="email"
                    placeholder="Email"
                    autoFocus
                    _container={{ mb: 4 }}
                  />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    name="password"
                    placeholder="Password"
                    _container={{ mb: 2 }}
                    icon={
                      <IconButton
                        variant="unstyled"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label="Hide/Show password"
                        icon={<Icon as={showPassword ? FaEye : FaEyeSlash} mt={1} />}
                      />
                    }
                  />
                  <Box textAlign={passwordlessDashboardAccessEnabled ? 'right' : 'left'} mb={2}>
                    <Link as={RRLink} to={FORGOT_PASSWORD}>
                      Forgot password?
                    </Link>
                  </Box>
                  <Button w="100%" mt={6} type="submit" isLoading={isSubmitting}>
                    LOGIN
                  </Button>
                  {error && (
                    <Text fontSize="14px" mt={2} color="leaseEndRed" textAlign="center">
                      {error}
                    </Text>
                  )}
                  <DividerWithText text="or" my={4} />
                  <Button
                    w="100%"
                    variant="secondary"
                    onClick={() => {
                      setShowOneClickLogin(true);
                      setPassedEmail(values.email);
                    }}
                  >
                    SIGN IN WITH ONE-TIME LINK
                  </Button>
                  <Box textAlign="center" mt={4}>
                    Don't have an account?{' '}
                    <Link as={RRLink} to={CREATE_ACCOUNT}>
                      Click to get started!
                    </Link>
                  </Box>
                </Box>
                <SimpleFooter />
              </Form>
            );
          }}
        </Formik>
      </Container>
    </>
  );
};

export default PasswordLogin;
